/**
 * @file     dateFormat.ts
 * @readme   ./README.md
 * @purpose  Deterministic date/time formatting for client components. ICU
 *           text output (month/weekday names, AM/PM spacing) drifts between
 *           Node and browser engines, which throws React #418 when a client
 *           component renders dates during SSR. These formatters use Intl
 *           ONLY for numeric timezone math and assemble all text by hand,
 *           so server and client output is byte-identical.
 *
 * Input semantics:
 * - Date-only ISO strings ("2026-06-10", "2026-06") are calendar dates and
 *   format in UTC, so the label never shifts a day/month with the viewer's
 *   timezone.
 * - Date objects, timestamps, and full ISO datetimes are moments and format
 *   in America/Vancouver (site-local), so server (UTC) and client (any
 *   timezone) agree.
 */

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const WEEKDAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAYS_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const SITE_TZ = 'America/Vancouver';
const DATE_ONLY_RE = /^\d{4}-\d{2}(-\d{2})?$/;

export type DateInput = Date | string | number;

export interface Parts {
  year: number;
  /** 1-12 */
  month: number;
  day: number;
  /** 0-23 */
  hour: number;
  /** zero-padded */
  minute: string;
  /** 0 = Sunday */
  weekday: number;
}

/**
 * Vancouver-local calendar parts for any instant. Exported so grid math derives
 * every boundary in SITE_TZ rather than the runtime's local zone.
 */
export function partsOf(input: DateInput): Parts | null {
  if (typeof input === 'string' && DATE_ONLY_RE.test(input.trim())) {
    const [y, m, d] = input.trim().split('-').map(Number);
    const day = d ?? 1;
    if (m < 1 || m > 12) return null;
    return { year: y, month: m, day, hour: 0, minute: '00', weekday: new Date(Date.UTC(y, m - 1, day)).getUTCDay() };
  }
  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) return null;
  const fields = new Intl.DateTimeFormat('en-US', {
    timeZone: SITE_TZ,
    year: 'numeric', month: 'numeric', day: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: false,
  }).formatToParts(date);
  const get = (type: string) => fields.find((p) => p.type === type)?.value ?? '';
  const year = Number(get('year'));
  const month = Number(get('month'));
  const day = Number(get('day'));
  if (!year || !month || !day) return null;
  return {
    year,
    month,
    day,
    // Some ICU versions report midnight as 24 with hour12: false.
    hour: Number(get('hour')) % 24,
    minute: get('minute').padStart(2, '0'),
    // Weekday is derived numerically from the timezone-local calendar date
    // instead of parsing ICU's weekday text.
    weekday: new Date(Date.UTC(year, month - 1, day)).getUTCDay(),
  };
}

/** "June 2026" */
export function formatMonthYearLong(input: DateInput): string {
  const p = partsOf(input);
  return p ? `${MONTHS_LONG[p.month - 1]} ${p.year}` : '';
}

/** "Jun 2026" */
export function formatMonthYear(input: DateInput): string {
  const p = partsOf(input);
  return p ? `${MONTHS_SHORT[p.month - 1]} ${p.year}` : '';
}

/** "Jun 10" */
export function formatMonthDay(input: DateInput): string {
  const p = partsOf(input);
  return p ? `${MONTHS_SHORT[p.month - 1]} ${p.day}` : '';
}

/** "Jun 10, 2026" */
export function formatShortDate(input: DateInput): string {
  const p = partsOf(input);
  return p ? `${MONTHS_SHORT[p.month - 1]} ${p.day}, ${p.year}` : '';
}

/** "June 10, 2026" */
export function formatLongDate(input: DateInput): string {
  const p = partsOf(input);
  return p ? `${MONTHS_LONG[p.month - 1]} ${p.day}, ${p.year}` : '';
}

/** "Wed" */
export function formatWeekdayShort(input: DateInput): string {
  const p = partsOf(input);
  return p ? WEEKDAYS_SHORT[p.weekday] : '';
}

/** "Wed, Jun 10" */
export function formatWeekdayMonthDay(input: DateInput): string {
  const p = partsOf(input);
  return p ? `${WEEKDAYS_SHORT[p.weekday]}, ${MONTHS_SHORT[p.month - 1]} ${p.day}` : '';
}

/** "Wednesday, June 10" */
export function formatWeekdayLongDate(input: DateInput): string {
  const p = partsOf(input);
  return p ? `${WEEKDAYS_LONG[p.weekday]}, ${MONTHS_LONG[p.month - 1]} ${p.day}` : '';
}

/** "9:30 PM" */
export function formatTime(input: DateInput): string {
  const p = partsOf(input);
  if (!p) return '';
  const ampm = p.hour >= 12 ? 'PM' : 'AM';
  const hour = p.hour % 12 === 0 ? 12 : p.hour % 12;
  return `${hour}:${p.minute} ${ampm}`;
}

/** "Jun 10, 2026, 9:30 PM" */
export function formatDateTime(input: DateInput): string {
  const p = partsOf(input);
  if (!p) return '';
  return `${formatShortDate(input)}, ${formatTime(input)}`;
}

/** "2026-06-10" */
export function formatNumericDate(input: DateInput): string {
  const p = partsOf(input);
  if (!p) return '';
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}
